import React from 'react';
import HBButton, { IButtonProps } from '../HBButton/HBButton';
import { MustIncludeOneOfBasicTestIdProps } from '../common/internal/test-id.types';
import type { TButtonSize } from '../HBButton/HBButton.types';
import { vars } from '../../theme';
import styled, { css } from 'styled-components';
import { newTextStyles } from '../HBText/HBText.styles';

export type LinkButtonTheme =
    | 'accent'
    | 'neutral'
    | 'solid'
    | 'secondary'
    | 'link-button-accent';

type TLinkButtonAlignment = 'start' | 'center' | 'end';

export interface HBLinkButtonProps
    extends Omit<IButtonProps, 'buttonTheme' | 'size'> {
    /** Restricted to sm/md/lg; controls text style (emphasis14/16/18 respectively). */
    size: Extract<TButtonSize, 'sm' | 'md' | 'lg'>;
    /** Visual theme mapped to an underlying HBButton buttonTheme; 'accent' renders as blue link text, 'neutral' as neutral-colored. */
    linkButtonTheme?: LinkButtonTheme;
    /** Horizontal alignment of the link text within the button container via justify-content. */
    alignment?: TLinkButtonAlignment;
    /**
     * @deprecated Use the `useDefaultFill` prop on individual icons from `@honeybook/icons` instead.
     * When true, allows icons to keep their default SVG fill instead of inheriting currentColor.
     */
    shouldUseIconDefaultFill?: boolean;
}

const HBLinkButton = ({
    children,
    linkButtonTheme = 'accent',
    alignment,
    ...props
}: HBLinkButtonProps & MustIncludeOneOfBasicTestIdProps) => {
    return (
        <StyledHBButton
            {...props}
            {...THEME_PROPS[linkButtonTheme]}
            $alignment={alignment}
        >
            {children}
        </StyledHBButton>
    );
};

export default HBLinkButton;

const StyledHBButton = styled(HBButton)<{
    $alignment?: TLinkButtonAlignment;
}>`
    height: fit-content;
    padding: 0;
    ${({ size }) => SIZE_PROPS[size]}

    ${({ $alignment }) =>
        $alignment &&
        css`
            justify-content: ${$alignment === 'start'
                ? 'flex-start'
                : $alignment === 'end'
                ? 'flex-end'
                : 'center'};
        `}
`;

const THEME_PROPS: Record<
    HBLinkButtonProps['linkButtonTheme'],
    Pick<IButtonProps, 'buttonTheme' | 'loaderColor'>
> = {
    accent: {
        buttonTheme: 'button-link-blue',
        loaderColor: vars.colors.text.accent
    },
    neutral: {
        buttonTheme: 'link-button-neutral'
    },
    solid: {
        buttonTheme: 'button-link-solid'
    },
    secondary: {
        buttonTheme: 'button-link-secondary'
    },
    'link-button-accent': {
        buttonTheme: 'link-button-accent'
    }
};

const SIZE_PROPS: Record<HBLinkButtonProps['size'], any> = {
    sm: newTextStyles.emphasis14,
    md: newTextStyles.emphasis16,
    lg: newTextStyles.emphasis18
};
